import { FormsyRadioGroup, Radio } from '@thanx/uikit/radio'
import {
  getPreferencesModalSetting,
  GET_PREFERENCES_MODAL_CONFIG_SUCCESS,
  updatePreferencesModalSetting,
  UPDATE_PREFERENCES_MODAL_CONFIG_SUCCESS,
} from 'actions/config'
import { alert } from 'actions/flash'
import ConfirmModal from 'components/ConfirmModal'
import Section from 'components/Section'
import Formsy from 'formsy-react'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import {
  PreferencesModalConfigApiFields,
  PreferencesModalSetting as SelectionType,
} from 'models/Config'
import React, { useCallback, useEffect, useState } from 'react'
import CmsPage from 'scenes/Cms/components/CmsPage'
import PublishBanner from 'scenes/Cms/components/PublishBanner'
import OrderingNotEnabledPage from '../OrderingNotEnabledPage'
import ExistingOrderTip from './ExisitingOrderTip'
import SelectionGroup from './SelectionGroup'
import Sidebar from './Sidebar'

const t = buildTranslate('cms.settings.preferences_modal_setting')

type FormData = {
  webSelection: SelectionType | undefined
  appSelection: SelectionType | undefined
}

const PreferencesModalSetting: React.FC<{ showTitle?: boolean }> = ({
  showTitle = true,
}) => {
  const merchant = useCurrentMerchant()
  const appSelectionEnabled = useFlag('app-preferences-modal-setting-mx', false)

  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [formData, setFormData] = useState<FormData>({
    webSelection: undefined,
    appSelection: undefined,
  })

  const displayAlert = useCallback(
    (key: 'danger' | 'success', messageKey: string) => {
      dispatch(
        alert({
          key,
          message: t(messageKey),
          timeout: 5,
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)
      const response = await dispatch(getPreferencesModalSetting())
      if (response.type === GET_PREFERENCES_MODAL_CONFIG_SUCCESS) {
        const preferences_modal: PreferencesModalConfigApiFields =
          response.payload.data.preferences_modal
        if (preferences_modal) {
          setFormData({
            webSelection: preferences_modal.setting,
            appSelection: preferences_modal.app_setting ?? SelectionType.NEVER,
          })
        }
      } else {
        displayAlert('danger', 'fetch_error')
      }
      setIsLoading(false)
    }

    fetchData()
  }, [dispatch, displayAlert])

  const handlePublish = async () => {
    setIsSubmitting(true)

    if (!formData.webSelection) {
      displayAlert('danger', 'form_error')
    } else {
      const response = await dispatch(
        updatePreferencesModalSetting(
          formData.webSelection,
          appSelectionEnabled ? formData.appSelection : undefined
        )
      )

      if (response.type === UPDATE_PREFERENCES_MODAL_CONFIG_SUCCESS) {
        displayAlert('success', 'submit_success')
      } else {
        displayAlert('danger', 'submit_error')
      }
    }

    setIsModalOpen(false)
    setIsSubmitting(false)
  }

  if (!merchant?.ordering_enabled) {
    return <OrderingNotEnabledPage />
  }

  return (
    <Formsy
      onValidSubmit={() => {
        setIsModalOpen(true)
      }}
      onChange={(newFormData: FormData) => {
        setFormData(newFormData)
      }}
      onInvalidSubmit={() =>
        dispatch(
          alert({
            key: 'danger',
            message: t('form_error'),
            timeout: 5,
          })
        )
      }
    >
      <CmsPage
        showTitle={showTitle}
        title={t('title')}
        description={t('description')}
        sidebar={<Sidebar isLoading={isSubmitting} disabled={false} />}
        isLoading={isLoading}
      >
        <ExistingOrderTip />

        <Section className="p-m mb-m">
          <h4 className="mb-s">{t('app_selection.title')}</h4>
          {appSelectionEnabled ? (
            <SelectionGroup
              name="appSelection"
              translationScope="app_selection"
              value={formData.appSelection}
            />
          ) : (
            <FormsyRadioGroup name="appSelection" value={SelectionType.NEVER}>
              <Radio value={SelectionType.NEVER}>
                {t('app_selection.never')}
              </Radio>
              <p className="body-text-4 grey-70 ml-l mt-xxs">
                {t('app_selection.never_explanation')}
              </p>
            </FormsyRadioGroup>
          )}
        </Section>

        <Section className="p-m mb-m">
          <h4 className="mb-s">{t('web_selection.title')}</h4>
          <SelectionGroup
            name="webSelection"
            translationScope="web_selection"
            value={formData.webSelection}
            recommended={SelectionType.FIRST_ORDER}
          />
        </Section>

        <PublishBanner
          buttonText={t('publish')}
          isLoading={isSubmitting}
          disabled={false}
        />
      </CmsPage>
      <ConfirmModal
        title={t('modal.publish_changes')}
        description={t('modal.you_are_publishing')}
        confirmText={t('modal.publish')}
        isOpen={isModalOpen}
        isLoading={isSubmitting}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => handlePublish()}
      />
    </Formsy>
  )
}

export default PreferencesModalSetting
